import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { injectable, inject } from 'src/features/ioc';
import { useManagedOAuth } from 'src/lib/featureFlags/flags/useManagedOAuth';
import type { OAuth2Service } from 'src/services/OAuth2Service';
import type { IsomorphicBindings } from 'src/types/bindings';

interface QueuedRequest {
    config: InternalAxiosRequestConfig;
    resolve: (value: any) => void;
    reject: (error: any) => void;
}

@injectable()
export class ApiAuthService {
    #isRefreshing = false;
    #log: IsomorphicBindings['Logger'];
    #requestQueue: QueuedRequest[] = [];

    constructor(
        @inject('OAuth2Service')
        private readonly oauth2Service: OAuth2Service,
        @inject('Logger') logger: IsomorphicBindings['Logger'],
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
    ) {
        this.#log = logger.getSubLogger({ name: 'ApiAuthService' }, { component: 'auth' });
        this.#initialize();
    }

    #initialize(): void {
        // Subscribe to auth events if managed OAuth features are enabled
        const useManagedOAuthFeatures = useManagedOAuth.select(this.store.getState());
        if (this.oauth2Service.type === 'managed' && useManagedOAuthFeatures) {
            this.oauth2Service.subscribeToAuthEvents((event) => {
                if (event.type === 'LOGOUT' || event.type === 'SESSION_EXPIRED') {
                    this.#clearQueue('Auth state cleared');
                }
            });

            if (TRACE) {
                this.#log.trace('ApiAuthService initialized with managed OAuth2Service and features enabled');
            }
        }
    }

    /**
     * Handles a request that received a 401 response, potentially refreshing the token
     * and retrying the request.
     *
     * @returns A promise that resolves to the retry response or rejects with the original error
     */
    async handleUnauthorizedRequest(error: AxiosError): Promise<any> {
        // Validate this is a 401 we should handle
        if (!this.#shouldHandleUnauthorized(error)) {
            throw error;
        }

        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Mark as retry to prevent infinite loops
        originalRequest._retry = true;

        // If already refreshing, queue this request
        if (this.#isRefreshing) {
            return this.#queueRequest(originalRequest);
        }

        // Managed OAuth features not enabled
        const useManagedOAuthFeatures = useManagedOAuth.select(this.store.getState());
        if (!useManagedOAuthFeatures) {
            if (TRACE) {
                this.#log.trace('Managed OAuth features disabled for 401 handling');
            }
            throw error;
        }

        // Start refresh process
        this.#isRefreshing = true;

        try {
            const result = await this.oauth2Service.handleUnauthorized();

            if (result.success && result.token) {
                // Update original request with new token
                originalRequest.headers['Authorization'] = `Bearer ${result.token}`;

                // Process queued requests with new token
                this.#processQueue(true, result.token);

                // Retry the original request using the same axios instance
                const axiosInstance = (originalRequest as any)._axiosInstance || axios;
                return axiosInstance(originalRequest);
            } else {
                // Token refresh failed
                this.#processQueue(false);
                throw error;
            }
        } catch (refreshError) {
            this.#log.warn('Token refresh failed', { error: refreshError });
            this.#processQueue(false);
            throw error;
        } finally {
            this.#isRefreshing = false;
        }
    }

    #shouldHandleUnauthorized(error: AxiosError): boolean {
        // Not a 401
        if (error.response?.status !== 401) {
            return false;
        }

        const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Already retried
        if (config._retry) {
            return false;
        }

        // Skip auth endpoints to prevent loops
        const url = config.url || '';
        if (url.includes('/oauth/') || url.includes('/token') || url.includes('/auth/')) {
            if (TRACE) {
                this.#log.trace('Skipping 401 handling for auth endpoint', { url });
            }
            return false;
        }

        return true;
    }

    #queueRequest(config: InternalAxiosRequestConfig): Promise<any> {
        return new Promise((resolve, reject) => {
            this.#requestQueue.push({
                config,
                resolve: (updatedConfig) => {
                    // Retry with updated config using the same axios instance
                    const axiosInstance = (updatedConfig as any)._axiosInstance || axios;
                    resolve(axiosInstance(updatedConfig));
                },
                reject,
            });
        });
    }

    #processQueue(success: boolean, token?: string): void {
        const queue = [...this.#requestQueue];
        this.#requestQueue = [];

        if (TRACE) {
            this.#log.trace('Processing request queue', {
                queueSize: queue.length,
                success,
            });
        }

        queue.forEach(({ config, resolve, reject }) => {
            if (success && token) {
                // Update config with new token
                config.headers['Authorization'] = `Bearer ${token}`;
                resolve(config);
            } else {
                reject(new Error('Token refresh failed'));
            }
        });
    }

    #clearQueue(reason: string): void {
        const queue = [...this.#requestQueue];
        this.#requestQueue = [];

        if (queue.length > 0) {
            this.#log.debug('Clearing request queue', {
                queueSize: queue.length,
                reason,
            });
        }

        queue.forEach(({ reject }) => {
            reject(new Error(reason));
        });
    }
}
